/** Insurance Management table columns — shared defs for list + column customizer. */

import { isCalendarDateBeforeToday } from "./dueDateTime";

export const POLICY_STATUSES = ["Active", "Expired", "Pending", "Cancelled"];

export const INSURANCE_TABLE_COLUMNS = [
  { id: "policy_number", label: "Policy number" },
  { id: "policyholder", label: "Policyholder" },
  { id: "insurance_provider", label: "Provider" },
  { id: "asset_id", label: "Asset" },
  { id: "vendor_id", label: "Vendor" },
  { id: "insured_asset", label: "Insured asset" },
  { id: "policy_period", label: "Period" },
  { id: "effective_date", label: "Effective" },
  { id: "expiration_date", label: "Expiration" },
  { id: "policy_premium", label: "Premium" },
  { id: "yearly_premium", label: "Yearly premium" },
  { id: "policy_status", label: "Status" },
];

export const INSURANCE_TABLE_DEFAULT_VISIBLE = [
  "policy_number",
  "insurance_provider",
  "policy_status",
  "policy_period",
  "effective_date",
  "expiration_date",
  "policy_premium",
  "yearly_premium",
  "asset_id",
  "vendor_id",
];


export const INSURANCE_TABLE_KEY = "insurance.table";

export const INSURANCE_SORT_KEY_TYPES = {
  effective_date: "date",
  expiration_date: "date",
  policy_premium: "number",
  yearly_premium: "number",
};

export const INSURANCE_NUMERIC_COLUMN_IDS = new Set(["policy_premium", "yearly_premium"]);

/**
 * Parse policy period text into a month count.
 * Supports e.g. "6 Months", "12 Months", "1 Year", "2 years", or a bare number (months).
 * @param {unknown} period
 * @returns {number|null}
 */
export function parseInsurancePeriodMonths(period) {
  const raw = period != null ? String(period).trim() : "";
  if (!raw) return null;

  const yearMatch = raw.match(/^(\d+(?:\.\d+)?)\s*years?$/i);
  if (yearMatch) {
    const years = Number(yearMatch[1]);
    if (!Number.isFinite(years) || years <= 0) return null;
    return years * 12;
  }

  const monthMatch = raw.match(/^(\d+(?:\.\d+)?)\s*months?$/i);
  if (monthMatch) {
    const months = Number(monthMatch[1]);
    if (!Number.isFinite(months) || months <= 0) return null;
    return months;
  }

  const bare = Number(raw);
  if (Number.isFinite(bare) && bare > 0) return bare;
  return null;
}

/**
 * Project policy premium to a 12-month yearly amount: premium * (12 / periodMonths).
 * e.g. 6 Months @ $345.39 → $690.78
 * @param {unknown} premium
 * @param {unknown} period
 * @returns {number|null}
 */
export function computeInsuranceYearlyPremium(premium, period) {
  const amt = Number(premium);
  if (!Number.isFinite(amt)) return null;
  const months = parseInsurancePeriodMonths(period);
  if (months == null || months <= 0) return null;
  return (amt * 12) / months;
}

/**
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {number|null}
 */
export function getInsuranceYearlyPremium(row) {
  return computeInsuranceYearlyPremium(row?.policy_premium, row?.policy_period);
}

/**
 * Sum yearly premiums for displayed/filtered rows (skips rows that cannot be projected).
 * @param {Array<Record<string, unknown>>} rows
 * @returns {{ totalYearlyPremium: number, counted: number }}
 */
export function summarizeInsuranceYearlyPremium(rows) {
  let totalYearlyPremium = 0;
  let counted = 0;
  (rows || []).forEach((row) => {
    const yearly =
      row?.yearly_premium != null && Number.isFinite(Number(row.yearly_premium))
        ? Number(row.yearly_premium)
        : getInsuranceYearlyPremium(row);
    if (yearly == null || !Number.isFinite(yearly)) return;
    totalYearlyPremium += yearly;
    counted += 1;
  });
  return { totalYearlyPremium, counted };
}

/**
 * True when policy expiration calendar day is strictly before today (local).
 * Uses UTC date-only helpers for stored expiration dates.
 * @param {unknown} expirationDate
 * @param {{ today?: import("dayjs").Dayjs }} [options]
 */
export function isInsuranceExpiredByExpirationDate(expirationDate, options = {}) {
  return isCalendarDateBeforeToday(expirationDate, options);
}
